import { z } from 'zod';
import { ROLE_ADDRESS_REFUSAL, isRoleAddress } from '../platform-email';

/**
 * The shop's "Customer replies go to" address (`organization.reply_to_email`, migration 0038):
 * the Reply-To on ticket, invoice and quote emails, so a customer's reply reaches the shop and
 * not the platform's mailbox (ops insights Phase 5b spec §1, §4).
 *
 * 🔴 It goes into a mail HEADER. This mirrors `organization_reply_to_email_check` rule for rule
 * (the CHECK is the copy SQL cannot import; `org-reply-to.test.ts` writes each refusal through
 * both): at most 254 characters, exactly one `@` with something either side, and no whitespace,
 * control character (CR/LF among them), `,`, `;`, `<`, `>` or `"`, any of which could end the
 * header or name a second address.
 *
 * Trimmed first. Blank, or `null`, clears it ("not set": ticket emails then wait unsent).
 */
export const REPLY_TO_EMAIL_MAX = 254;

// eslint-disable-next-line no-control-regex
const HEADER_UNSAFE = /[\s\u0000-\u001f\u007f,;<>"]/;

export const replyToEmailField = z
  .string()
  .trim()
  .max(REPLY_TO_EMAIL_MAX)
  .refine((v) => v === '' || /^[^@]+@[^@]+$/.test(v), {
    message: 'Enter one email address, like office@yourshop.com',
  })
  .refine((v) => !HEADER_UNSAFE.test(v), {
    message: 'An email address has no spaces or any of , ; < > "',
  })
  .transform((v) => (v === '' ? null : v))
  .nullable();

/**
 * "Customer replies go to", as typed (MAP 249 spec §5): the address a confirmation link is sent
 * to, before it is used. `replyToEmailField`'s header rules, and it must be an address a person
 * reads (`isRoleAddress`). Clearing it is its own action, so blank is refused here.
 */
export const replyAddressInput = z.object({
  email: replyToEmailField
    .refine((v): v is string => v !== null, {
      message: 'Enter one email address, like office@yourshop.com',
    })
    .refine((v) => v === null || !isRoleAddress(v), { message: ROLE_ADDRESS_REFUSAL }),
});
export type ReplyAddressInput = z.infer<typeof replyAddressInput>;
